import React from "react"

import { StrategyPineDetailDialog } from "@/components/Strategies/StrategyPineDetailDialog"
import { Button } from "@/components/ui/button"
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field"
import { Input } from "@/components/ui/input"

import { DashboardLayoutDialogFooter } from "./DashboardLayoutDialogFooter"
import {
  DASHBOARD_LAYOUT_FIELD_DESC_CLASS,
  DASHBOARD_LAYOUT_FIELD_LABEL_CLASS,
} from "./dashboardLayoutUi"

export type DashboardRenameLayoutDialogIntent = "toolbar" | "createBeforeEdit"

export interface DashboardRenameLayoutDialogProps {
  open: boolean
  intent: DashboardRenameLayoutDialogIntent
  value: string
  onValueChange: (value: string) => void
  onOpenChange: (open: boolean) => void
  onCancel: () => void
  onSave: () => void
}

const DashboardRenameLayoutDialog: React.FC<DashboardRenameLayoutDialogProps> = ({
  open,
  intent,
  value,
  onValueChange,
  onOpenChange,
  onCancel,
  onSave,
}) => {
  const isCreate = intent === "createBeforeEdit"
  const title = isCreate ? "Name new layout" : "Rename layout"
  const description = isCreate
    ? "Choose a name for your new dashboard layout before editing."
    : "Update the display name for this layout."
  const fieldLabel = isCreate ? "Layout name" : "New layout name"
  const saveLabel = isCreate ? "Create layout" : "Save"

  return (
    <StrategyPineDetailDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onCancel()
        onOpenChange(nextOpen)
      }}
      title={title}
      showHeader={false}
      showDismissButton={false}
      contentClassName="sm:max-w-md"
      bodyClassName="flex-none overflow-visible"
      footer={
        <DashboardLayoutDialogFooter onCancel={onCancel}>
          <Button
            type="button"
            variant="gradient"
            size="sm"
            data-font="primary"
            className="shrink-0"
            disabled={!value.trim()}
            onClick={onSave}
          >
            <span data-font="primary">{saveLabel}</span>
          </Button>
        </DashboardLayoutDialogFooter>
      }
    >
      <Field className="gap-2">
        <FieldLabel htmlFor="dashboard-layout-rename-input" className={DASHBOARD_LAYOUT_FIELD_LABEL_CLASS}>
          {fieldLabel}
        </FieldLabel>
        <FieldDescription className={DASHBOARD_LAYOUT_FIELD_DESC_CLASS}>{description}</FieldDescription>
        <Input
          id="dashboard-layout-rename-input"
          value={value}
          onChange={(event) => onValueChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") onSave()
            if (event.key === "Escape") onCancel()
          }}
          autoFocus
          data-font="primary"
        />
      </Field>
    </StrategyPineDetailDialog>
  )
}

export default DashboardRenameLayoutDialog
